/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2023 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import { Like } from "../entities/like.entity";
import { Permission } from "../entities/permission.entity";
import { BasePlateSnapshot, Plate, PlateMetadata, PlateSnapshot } from "../entities/plate.entity";

import { BlockConverter } from "./BlockConverter";

export class PlateConverter {
  /* -------------------------------------------------- */
  /*                     Conversion                     */
  /* -------------------------------------------------- */
  public static toSnapshot(backendPlate: Plate, currentUserId: string | null): PlateSnapshot {
    const snapshot: PlateSnapshot = {
      ...this.toBaseSnapshot(backendPlate, currentUserId),
      likes: backendPlate.likedBy || [], // @todo - not optional
      height: backendPlate.height,
      width: backendPlate.width,
      selection: [],
      initialBlockSnapshots: (backendPlate.blocks || []).map((block) =>
        BlockConverter.toSnapshot(block, currentUserId)
      ),
      blockIds: (backendPlate.blocks || []).map((block) => block.id),
      initialEditors: backendPlate.Permission?.editors || [], // much like blocks, these will be pulled off and turned into classes when the plate is instantiated
      editorIds: (backendPlate.Permission?.editors || []).map((editor) => editor.id),
      modal: null,
    };
    return snapshot;
  }

  public static toBaseSnapshot(
    backendPlate: Plate | PlateMetadata,
    currentUserId: string | null
  ): BasePlateSnapshot {
    const snapshot: BasePlateSnapshot = {
      id: backendPlate.id,
      title: backendPlate.title,
      createdAt: backendPlate.createdAt.toString(),
      updatedAt: backendPlate.updatedAt.toString(),
      screenshotTime: backendPlate.screenshotTime?.toString() || null,
      owner: backendPlate.user,
      currentUserIsOwner: backendPlate.user.id === currentUserId,
      // initialEditors: backendPlate.Permission?.editors || [], // much like blocks, these will be pulled off and turned into classes when the plate is instantiated
      editable: this.getIsEditable(backendPlate.Permission || null, currentUserId),
      isPublic: backendPlate.isPublic,
      isLiked: this.getIsLiked(backendPlate.likedBy || [], currentUserId),
    };
    return snapshot;
  }

  /* -------------------------------------------------- */
  /*                       Helpers                      */
  /* -------------------------------------------------- */
  private static getIsEditable(permission: Permission | null, currentUserId: string | null): boolean {
    if (!currentUserId) return false;
    if (permission?.ownerId === currentUserId) return true; // true if owner
    if (permission?.editors?.some((editor) => editor.id === currentUserId)) return true; // true if editor
    return false;
  }

  private static getIsLiked(likes: Like[], currentUserId: string | null) {
    if (!currentUserId) return false;
    return likes.some((like) => like.userId === currentUserId);
  }

  /* -------------------------------------------------- */
  /*                      Jank!                        */
  /* -------------------------------------------------- */
  // public static doJankConversionToBasePlates(pages?: Plate[] | PlateMetadata[]): BasePlate[] {
  //   const plates = (pages || []).map((plate) => {
  //     const snapshot = this.toBaseSnapshot(plate);
  //     return new BasePlate(snapshot);
  //   });
  //   return plates;
  // }
}
